import { css, keyframes } from '@emotion/react';

import { media } from '../shared/styles';
import type { ModalSize } from './Modal.types';

// Fade-in animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

// Scale and fade animation for modal container
const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

// Scroll layer (backdrop) - keeps animation and background styles
export const scrollLayerStyles = css`
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  animation: ${fadeIn} 0.15s ease-out forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// Container - keeps animation, background, shadow, and border-radius
export const containerStyles = css`
  background-color: var(--surface-primary, #ffffff);
  border-radius: var(--radius-lg, 12px);
  max-height: calc(100dvh - var(--spacing-8));
  box-shadow: var(--shadow-xl, 0 20px 40px 0 rgba(0, 0, 0, 0.3));
  animation: ${fadeInScale} 0.2s ease-out 0.05s both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const sizeStyles: Record<ModalSize, ReturnType<typeof css>> = {
  sm: css`
    width: 100%;
    max-width: 24rem; /* 384px */
    padding: var(--spacing-5);
  `,
  md: css`
    width: 100%;
    max-width: 32rem; /* 512px */
    padding: var(--spacing-6);
  `,
  lg: css`
    width: 100%;
    max-width: 48rem; /* 768px */
    padding: var(--spacing-6);

    ${media.lg} {
      padding: var(--spacing-8);
    }
  `,
  xl: css`
    width: 100%;
    max-width: 64rem; /* 1024px */
    padding: var(--spacing-6);

    ${media.lg} {
      padding: var(--spacing-8);
    }
  `,
  full: css`
    width: calc(100vw - var(--spacing-8));
    height: calc(100dvh - var(--spacing-8));
    max-width: none;
    max-height: none;
    padding: 0;
    border-radius: var(--radius-md, 8px);
  `,
};

export const closeButtonStyles = css`
  position: absolute;
  top: var(--spacing-4);
  right: var(--spacing-4);
  z-index: 1;
`;

export const closeButtonFullStyles = css`
  top: var(--spacing-3);
  right: var(--spacing-3);
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: var(--radius-xxl, 25px);
  padding: var(--spacing-2) var(--spacing-4);

  &:hover {
    background-color: var(--color-base-white, #ffffff);
  }
`;
